// ECharts paints to canvas/SVG, so every colour must be a literal string read at draw time. Falls
// back to today's light literals when a `--color-*` variable is empty, keeping light mode unchanged.
export interface ChartTokens {
  text: string;
  textMuted: string;
  axisLine: string;
  splitLine: string;
  splitLineSubtle: string;
  tooltipBg: string;
  tooltipText: string;
  primaryBar: string;
  highlight: string;
  pointBorder: string;
  series: string[];
  colorSets: string[][];
  hairlineBorder: string;
}

export function readVar(name: string, fallback: string): string {
  if (typeof document === "undefined") return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
}

export function readChartTokens(): ChartTokens {
  const isDark = typeof document !== "undefined" && document.documentElement.classList.contains("dark");

  const text = readVar("--color-type-default", "#1e293b");

  return {
    text,
    textMuted: readVar("--color-type-muted", "#5e6e82"),
    // The category axis line is deliberately dark (it doubles as the zero baseline on
    // charts that cross zero), so it shares the text token rather than the subtler gridline one.
    axisLine: text,
    splitLine: readVar("--color-slate-300", "#cbd5e1"),
    splitLineSubtle: readVar("--color-slate-200", "#e2e8f0"),
    // Tooltip is an inverted surface by design: dark-on-light in light mode, light-on-dark in dark.
    tooltipBg: text,
    tooltipText: readVar("--color-slate-0", "#ffffff"),
    // Dark blue-600 is tuned pale for link text, so solid bars use 400, the dark ramp's most vivid step.
    primaryBar: isDark ? readVar("--color-app-blue-400", "#4974f6") : readVar("--color-app-blue-600", "#2036ff"),
    // "#3b82f6" is a one-off light-mode accent; dark uses 600 so the active bar stays distinct from 400.
    highlight: isDark ? readVar("--color-app-blue-600", "#7ba0ff") : "#3b82f6",
    pointBorder: readVar("--color-slate-0", "#ffffff"),
    series: [
      isDark ? readVar("--color-app-blue-400", "#4974f6") : readVar("--color-app-blue-600", "#2036ff"),
      readVar("--color-app-purple-500", "#a072da"),
      readVar("--color-app-green-500", "#6aae34"),
      readVar("--color-app-yellow-500", "#ef9f11"),
      readVar("--color-app-red-500", "#ee4745"),
    ],
    // Position 1 is read as "the vivid end" (chroma.scale(...)(1), value/max -> 1); the dark ramp
    // inverts shade-to-lightness, so it goes [700, 400] here to keep position 1 on the vivid shade.
    colorSets: (
      [
        ["blue", "#97bbff", "#1d2cb6"],
        ["green", "#b2df8a", "#34551f"],
        ["yellow", "#f8d764", "#8e4212"],
        ["red", "#fb9a99", "#981d1c"],
        ["purple", "#d5c1f1", "#6a3d9a"],
      ] as const
    ).map(([family, lightStart, lightEnd]) =>
      isDark
        ? [readVar(`--color-app-${family}-700`, lightEnd), readVar(`--color-app-${family}-400`, lightStart)]
        : [readVar(`--color-app-${family}-300`, lightStart), readVar(`--color-app-${family}-800`, lightEnd)]
    ),
    // A hairline segment border needs to sit on the OPPOSITE side of the fill in each theme —
    // black-ish over a light fill, white-ish over a dark one.
    hairlineBorder: isDark ? "rgba(255, 255, 255, 0.15)" : "rgba(0, 0, 0, 0.15)",
  };
}
